import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { Button, Modal, Form, Row, Col, Spinner, Card } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { Add, Calendar, Trash } from 'iconsax-react';
import Select from 'react-select';
import MyApiClient from '../../../api-client/MyApiClient';
import { sidSelectStyles } from '../Util/sidSelectStyles';
import chiediConferma from '../Util/chiediConferma';

const dayOptions = [
    { value: 'MONDAY', label: 'Lunedì' },
    { value: 'TUESDAY', label: 'Martedì' },
    { value: 'WEDNESDAY', label: 'Mercoledì' },
    { value: 'THURSDAY', label: 'Giovedì' },
    { value: 'FRIDAY', label: 'Venerdì' },
    { value: 'SATURDAY', label: 'Sabato' },
    { value: 'SUNDAY', label: 'Domenica' }
];

// getDay() di JS: 0 = domenica
const GIORNI_JS = ['SUNDAY', 'MONDAY', 'TUESDAY', 'WEDNESDAY', 'THURSDAY', 'FRIDAY', 'SATURDAY'];

const statusLabels = {
    DRAFT: { label: 'Bozza', cls: 'text-warning' },
    ACTIVE: { label: 'Attiva', cls: 'text-success' },
    CANCELLED: { label: 'Cancellata', cls: 'text-danger' }
};

// Menu della tendina fuori dalla tabella (che ha overflow) e sopra le modal
const selectStyles = { ...sidSelectStyles, menuPortal: (base) => ({ ...base, zIndex: 2000 }) };

const pad = (n) => String(n).padStart(2, '0');
const isoGiorno = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
const hhmm = (d) => `${pad(d.getHours())}:${pad(d.getMinutes())}`;
const ora = (t) => (t || '').slice(0, 5);
// "2026-09-01" -> "01/09/2026" senza passare da Date (niente sorprese di fuso)
const dataIt = (s) => {
    const [y, m, d] = (s || '').slice(0, 10).split('-');
    return y && m && d ? `${d}/${m}/${y}` : s || '';
};

const getToken = () => {
    const tokenDetailsString = sessionStorage.getItem('userDetails');
    return tokenDetailsString ? JSON.parse(tokenDetailsString).idToken : null;
};

// Orari raggruppati per giorno, nell'ordine della settimana: "Lunedì 08:00–12:00, 15:00–22:00"
const righeOrari = (items = []) => dayOptions
    .map((day) => {
        const fasce = items
            .filter((h) => h.dayOfWeek === day.value)
            .sort((a, b) => ora(a.openTime).localeCompare(ora(b.openTime)))
            .map((h) => `${ora(h.openTime)}–${ora(h.closeTime)}`);
        return fasce.length ? { giorno: day.label, fasce: fasce.join(', ') } : null;
    })
    .filter(Boolean);

// Sedute che con gli orari del modulo resterebbero fuori dalla disponibilita
const seduteScoperte = (sedute, form) => sedute.filter((s) => {
    const inizio = new Date(s.dataInizio);
    const fine = new Date(s.dataFine);
    const giorno = isoGiorno(inizio);
    if (!form.startDate || !form.endDate || giorno < form.startDate || giorno > form.endDate) return true;
    if (isoGiorno(fine) !== giorno) return true;
    const dow = GIORNI_JS[inizio.getDay()];
    const da = hhmm(inizio);
    const a = hhmm(fine);
    return !(form.openingHoursItems || []).some((h) =>
        h.dayOfWeek === dow && h.openTime && h.closeTime && ora(h.openTime) <= da && a <= ora(h.closeTime));
});

// Lunedi'-domenica con l'orario predefinito (nuova disponibilita e "Genera per tutti i giorni")
const settimanaPredefinita = () => dayOptions.map((day, index) => ({
    id: (Date.now() + index).toString(),
    dayOfWeek: day.value,
    openTime: '08:00',
    closeTime: '20:00'
}));

// Seduta nella fascia: stesso giorno della settimana, inizio e fine dentro l'orario, nello stesso giorno
const seduteInFascia = (s, dayOfWeek, openTime, closeTime) => {
    const inizio = new Date(s.dataInizio);
    const fine = new Date(s.dataFine);
    if (!dayOfWeek || !openTime || !closeTime) return false;
    if (isoGiorno(inizio) !== isoGiorno(fine) || GIORNI_JS[inizio.getDay()] !== dayOfWeek) return false;
    return ora(openTime) <= hhmm(inizio) && hhmm(fine) <= ora(closeTime);
};

const descriviSeduta = (s) => {
    const inizio = new Date(s.dataInizio);
    const fine = new Date(s.dataFine);
    return `${pad(inizio.getDate())}/${pad(inizio.getMonth() + 1)} ${hhmm(inizio)}-${hhmm(fine)} ${s.titolo || ''}`.trim();
};

/*
 * Solo in sviluppo: con ?simulaPrenotazione=1 nell'indirizzo la disponibilita' aperta in modifica
 * ha una seduta finta di un corso nella prima fascia oraria, per vedere il blocco della riga
 * senza dover creare prenotazioni vere. Nella build pubblicata non esiste.
 */
const simulaPrenotazione = () => process.env.NODE_ENV === 'development'
    && new URLSearchParams(window.location.search).get('simulaPrenotazione') === '1';

const sedutaSimulata = (fasce) => {
    const prima = (fasce || []).find((h) => h._orig && h._orig.dayOfWeek && h._orig.openTime && h._orig.closeTime);
    if (!prima) return [];
    const { dayOfWeek, openTime } = prima._orig;
    const giorno = new Date();
    while (GIORNI_JS[giorno.getDay()] !== dayOfWeek) giorno.setDate(giorno.getDate() + 1);
    const [h, m] = ora(openTime).split(':').map(Number);
    const inizio = new Date(giorno); inizio.setHours(h, m, 0, 0);
    const fine = new Date(inizio); fine.setMinutes(fine.getMinutes() + 60);
    return [{ id: 'simulata', type: 'corso', titolo: 'Corso di prova (simulazione)', dataInizio: inizio.toISOString(), dataFine: fine.toISOString() }];
};

const TIPI_PRODOTTO = { corso: 'Corso', lezione: 'Lezione', impianto: 'Prenotazione impianto', generico: 'Evento' };

// Sedute prenotate raggruppate per prodotto presunto (risolto poi col dettaglio di una sola seduta).
// Le prenotazioni d'impianto sono ognuna un prodotto a se'.
const chiaveGruppo = (e) => (e.type === 'impianto' ? `impianto|${e.id}` : `${e.type || 'generico'}|${e.titolo || ''}|${e.tipologia || ''}`);

// Dal dettaglio di una seduta al prodotto da aprire (stessa logica di DettaglioEventoModal "Vai al ...")
const prodottoDaDettaglio = (e, d, corsi) => {
    const tipo = e.type || 'generico';
    if (tipo === 'corso') {
        const id = d?.corso?.id;
        const corso = id ? corsi.find((c) => c.id === id) : null;
        const base = corso?.type === 'CLOSED' ? '/struttura/booking/coursesviewclosed/' : '/struttura/booking/coursesview/';
        return {
            tipo,
            id: id || null,
            nome: corso?.name || d?.corso?.nome || e.titolo || 'Corso',
            dettaglio: d?.squadra?.nome && d.squadra.nome !== (corso?.name || d?.corso?.nome) ? d.squadra.nome : '',
            url: id ? `${base}${id}?tab=pianificazione` : null
        };
    }
    if (tipo === 'lezione') {
        const id = d?.lezione?.id;
        return {
            tipo,
            id: id || null,
            nome: d?.lezione?.nome || e.titolo || 'Lezione',
            dettaglio: d?.tipoLezione || e.tipologia || '',
            url: id ? `/struttura/booking/lessonsview/${id}` : null
        };
    }
    if (tipo === 'impianto') {
        // sourceId e' la vendita (userVenue) se esiste, altrimenti ricade sull'id della seduta
        const vendita = d?.sourceId && d.sourceId !== (d?.id || e.id) ? d.sourceId : null;
        const cliente = (d?.partecipanti || [])[0]?.nome || '';
        return {
            tipo,
            id: vendita || e.id,
            nome: cliente ? `Prenotazione di ${cliente}` : 'Prenotazione impianto',
            dettaglio: d?.tipologia || e.tipologia || '',
            url: vendita ? `/struttura/booking/uservenues/${vendita}` : null
        };
    }
    return { tipo: 'generico', id: null, nome: e.titolo || 'Evento', dettaglio: e.tipologia || '', url: null };
};

const DisponibilitaImpianto = ({ venueId, readonly = false }) => {
    const navigate = useNavigate();
    const [disponibilita, setDisponibilita] = useState([]);
    const [loading, setLoading] = useState(false);
    // Un solo pannello aperto alla volta: nuova disponibilita (in cima) o modifica sotto la riga
    const [nuova, setNuova] = useState(false);
    const [editingItem, setEditingItem] = useState(null);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [itemToDelete, setItemToDelete] = useState(null);
    const [showCancelModal, setShowCancelModal] = useState(false);
    const [itemToCancel, setItemToCancel] = useState(null);
    const [errorMessage, setErrorMessage] = useState('');
    // Sedute future prenotate sull'impianto, nei periodi di tutte le disponibilita non finite
    const [prenotate, setPrenotate] = useState({ caricate: false, lista: [], errore: '' });
    // Prodotti collegati risolti per gruppo di sedute (cache)
    const [risolti, setRisolti] = useState({});
    const richiestiRef = useRef(new Set());
    const corsiRef = useRef(null);

    const [formData, setFormData] = useState({
        startDate: '',
        endDate: '',
        openingHoursItems: []
    });

    const fetchDisponibilita = useCallback(async () => {
        try {
            setLoading(true);
            const token = getToken();
            if (token) {
                const apiClient = new MyApiClient();
                const response = await apiClient.getVenueSchedules(token, venueId);
                if (response.error) {
                    console.error('Errore API:', response.error);
                    setDisponibilita([]);
                } else {
                    const data = response.data || response || [];
                    setDisponibilita(Array.isArray(data) ? data : []);
                }
            }
        } catch (error) {
            console.error('Errore nel caricamento delle disponibilità:', error);
        } finally {
            setLoading(false);
        }
    }, [venueId]);

    useEffect(() => {
        if (venueId) {
            fetchDisponibilita();
        }
    }, [venueId, fetchDisponibilita]);

    const oggi = isoGiorno(new Date());

    // Righe il cui periodo non e' finito: una sola chiamata al calendario sull'unione dei periodi
    const righeDaVerificare = useMemo(() => disponibilita.filter((d) =>
        d.status !== 'CANCELLED' && d.startDate && d.endDate && d.endDate.slice(0, 10) >= oggi), [disponibilita, oggi]);

    useEffect(() => {
        if (!venueId || righeDaVerificare.length === 0) {
            setPrenotate({ caricate: true, lista: [], errore: '' });
            return undefined;
        }
        let annullato = false;
        const da = righeDaVerificare.reduce((min, d) => {
            const inizio = d.startDate.slice(0, 10) > oggi ? d.startDate.slice(0, 10) : oggi;
            return inizio < min ? inizio : min;
        }, '9999-12-31');
        const a = righeDaVerificare.reduce((max, d) => (d.endDate.slice(0, 10) > max ? d.endDate.slice(0, 10) : max), oggi);
        setPrenotate((p) => ({ ...p, caricate: false }));
        (async () => {
            try {
                const res = await new MyApiClient().getCalendarioEventi(getToken(), `${da}T00:00:00`, `${a}T23:59:59`, { impianti: venueId });
                if (annullato) return;
                const adesso = new Date();
                if (res?.error) {
                    setPrenotate({ caricate: true, lista: [], errore: res.error });
                    return;
                }
                const lista = (res?.eventi || [])
                    .filter((e) => e.dataInizio && e.dataFine && new Date(e.dataFine) > adesso)
                    .sort((x, y) => new Date(x.dataInizio) - new Date(y.dataInizio));
                setPrenotate({ caricate: true, lista, errore: '' });
            } catch (e) {
                if (!annullato) setPrenotate({ caricate: true, lista: [], errore: 'Verifica sedute non disponibile' });
            }
        })();
        return () => { annullato = true; };
    }, [righeDaVerificare, venueId, oggi]);

    // Sedute future di ogni riga: dal giorno piu' tardo tra oggi e l'inizio, fino alla fine del periodo
    const impegni = useMemo(() => {
        const mappa = {};
        righeDaVerificare.forEach((d) => {
            const inizio = d.startDate.slice(0, 10) > oggi ? d.startDate.slice(0, 10) : oggi;
            const fine = d.endDate.slice(0, 10);
            const eventi = prenotate.lista.filter((e) => {
                const giorno = isoGiorno(new Date(e.dataInizio));
                return giorno >= inizio && giorno <= fine;
            });
            if (eventi.length) mappa[d.id] = eventi;
        });
        return mappa;
    }, [righeDaVerificare, prenotate.lista, oggi]);

    // La verifica serve per una disponibilita attiva il cui periodo arriva a oggi o oltre
    // (le sedute sono prenotate solo sulle disponibilita attive)
    const verificaAttiva = !!editingItem && editingItem.status === 'ACTIVE' && !!editingItem.endDate && editingItem.endDate.slice(0, 10) >= oggi;
    // Sedute prenotate nel periodo attuale della disponibilita aperta, da oggi in poi
    const sedute = useMemo(() => {
        const lista = verificaAttiva ? impegni[editingItem.id] || [] : [];
        return {
            inCorso: verificaAttiva && !prenotate.caricate,
            lista: simulaPrenotazione() && editingItem ? [...lista, ...sedutaSimulata(formData.openingHoursItems)] : lista,
            errore: verificaAttiva ? prenotate.errore : ''
        };
        // la seduta simulata si aggancia alla prima fascia originale: basta ricalcolarla all'apertura
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [verificaAttiva, prenotate.caricate, prenotate.errore, impegni, editingItem]);

    // Sedute che cadono in ciascuna fascia oraria originale della disponibilita aperta:
    // le fasce con sedute sono impegnate e non si toccano
    const seduteFascia = useCallback((h) => {
        if (!h?._orig) return [];
        const { dayOfWeek, openTime, closeTime } = h._orig;
        return sedute.lista.filter((s) => seduteInFascia(s, dayOfWeek, openTime, closeTime));
    }, [sedute.lista]);

    const eventiBloccati = useMemo(() => {
        const visti = new Set();
        const out = [];
        formData.openingHoursItems.forEach((h) => seduteFascia(h).forEach((e) => {
            if (!visti.has(e.id)) {
                visti.add(e.id);
                out.push(e);
            }
        }));
        return out;
    }, [formData.openingHoursItems, seduteFascia]);

    // Risolve (una volta sola, poi in cache) il prodotto di ciascun gruppo di sedute delle fasce impegnate
    useEffect(() => {
        const daRisolvere = [];
        eventiBloccati.forEach((e) => {
            const k = chiaveGruppo(e);
            if (!richiestiRef.current.has(k)) {
                richiestiRef.current.add(k);
                daRisolvere.push({ k, e });
            }
        });
        if (daRisolvere.length === 0) return;
        const token = getToken();
        const api = new MyApiClient();
        (async () => {
            if (!corsiRef.current && daRisolvere.some(({ e }) => e.type === 'corso')) {
                corsiRef.current = api.getBookingCourses(token)
                    .then((r) => (Array.isArray(r) ? r : []))
                    .catch(() => []);
            }
            const corsi = corsiRef.current ? await corsiRef.current : [];
            await Promise.all(daRisolvere.map(async ({ k, e }) => {
                let d = null;
                if (e.type !== 'generico') {
                    try {
                        const res = await api.getCalendarioEventoById(token, e.id);
                        d = res?.error ? null : res;
                    } catch (err) {
                        d = null;
                    }
                }
                const prodotto = prodottoDaDettaglio(e, d, corsi);
                setRisolti((r) => ({ ...r, [k]: prodotto }));
            }));
        })();
    }, [eventiBloccati]);

    const scoperte = useMemo(() => seduteScoperte(sedute.lista, formData), [sedute.lista, formData]);

    const apriNuova = () => {
        setErrorMessage('');
        setEditingItem(null);
        setFormData({
            startDate: '',
            endDate: '',
            // Settimana intera gia' compilata, come "Genera per tutti i giorni" su un elenco vuoto
            openingHoursItems: settimanaPredefinita()
        });
        setNuova(true);
    };

    // Riga cliccata: apre il pannello (o lo chiude se era gia' aperto)
    const apriPannello = (item) => {
        if (readonly) return;
        setErrorMessage('');
        setNuova(false);
        if (editingItem?.id === item.id) {
            setEditingItem(null);
            return;
        }
        setEditingItem(item);
        setFormData({
            startDate: item.startDate ? item.startDate.slice(0, 10) : '',
            endDate: item.endDate ? item.endDate.slice(0, 10) : '',
            openingHoursItems: (item.openingHoursItems || []).map((h, i) => ({
                ...h,
                id: h.id || `${Date.now()}-${i}`,
                openTime: ora(h.openTime),
                closeTime: ora(h.closeTime),
                // Valori salvati: decidono se la fascia e' impegnata da sedute prenotate
                _orig: { dayOfWeek: h.dayOfWeek, openTime: ora(h.openTime), closeTime: ora(h.closeTime) }
            }))
        });
    };

    const chiudi = () => {
        setErrorMessage('');
        setNuova(false);
        setEditingItem(null);
    };

    const validaForm = () => {
        if (!formData.startDate || !formData.endDate) {
            setErrorMessage('Per favore, inserisci le date di inizio e fine.');
            return false;
        }
        if (!formData.openingHoursItems || formData.openingHoursItems.length === 0) {
            setErrorMessage('Per favore, aggiungi almeno un orario di apertura.');
            return false;
        }
        for (let i = 0; i < formData.openingHoursItems.length; i++) {
            const item = formData.openingHoursItems[i];
            if (!item.dayOfWeek || !item.openTime || !item.closeTime) {
                setErrorMessage(`Per favore, completa tutti i campi per l'orario ${i + 1}.`);
                return false;
            }
            if (item.closeTime <= item.openTime) {
                setErrorMessage(`Orario ${i + 1}: l'ora di chiusura deve venire dopo l'ora di apertura.`);
                return false;
            }
        }
        if (formData.endDate < formData.startDate) {
            setErrorMessage('La data di fine deve venire dopo la data di inizio.');
            return false;
        }
        return true;
    };

    const datiSchedule = () => ({
        venueId: venueId,
        // Una disponibilita attiva resta attiva: non la si riporta in bozza
        status: editingItem ? editingItem.status : 'DRAFT',
        startDate: formData.startDate,
        endDate: formData.endDate,
        openingHoursItems: formData.openingHoursItems.map(item => ({
            dayOfWeek: item.dayOfWeek,
            openTime: item.openTime,
            closeTime: item.closeTime
        }))
    });

    const handleSave = async () => {
        if (!validaForm()) return;
        try {
            setLoading(true);
            setErrorMessage('');
            const token = getToken();
            if (!token) return;
            const apiClient = new MyApiClient();
            const scheduleData = datiSchedule();
            const response = editingItem
                ? await apiClient.updateVenueSchedule(token, editingItem.id, scheduleData)
                : await apiClient.createVenueSchedule(token, scheduleData);
            if (response.error) {
                // Messaggio del backend cosi' com'e' (es. sedute prenotate che resterebbero fuori)
                setErrorMessage(response.error);
                return;
            }
            chiudi();
            await fetchDisponibilita();
        } catch (error) {
            console.error('Errore nel salvataggio:', error);
            setErrorMessage('Errore durante il salvataggio: ' + error.message);
        } finally {
            setLoading(false);
        }
    };

    const handleSaveAndActivate = async () => {
        if (!validaForm()) return;
        try {
            setLoading(true);
            setErrorMessage('');
            const token = getToken();
            if (!token) return;
            const apiClient = new MyApiClient();
            const scheduleData = { ...datiSchedule(), status: 'DRAFT' };

            let saveResponse;
            let scheduleId;
            if (editingItem) {
                saveResponse = await apiClient.updateVenueSchedule(token, editingItem.id, scheduleData);
                scheduleId = editingItem.id;
            } else {
                saveResponse = await apiClient.createVenueSchedule(token, scheduleData);
                scheduleId = saveResponse.data?.id;
            }
            if (saveResponse.error) {
                setErrorMessage(saveResponse.error);
                return;
            }
            if (!scheduleId) {
                setErrorMessage('Errore: ID della schedulazione non trovato dopo il salvataggio');
                return;
            }
            const activateResponse = await apiClient.activateVenueSchedule(token, scheduleId);
            if (activateResponse.error) {
                setErrorMessage('Salvataggio completato, ma errore durante l\'attivazione: ' + activateResponse.error);
                await fetchDisponibilita();
                return;
            }
            chiudi();
            await fetchDisponibilita();
        } catch (error) {
            console.error('Errore nel salvataggio e attivazione:', error);
            setErrorMessage('Errore durante il salvataggio e attivazione: ' + error.message);
        } finally {
            setLoading(false);
        }
    };

    const handleDelete = async () => {
        try {
            setLoading(true);
            const token = getToken();
            if (token && itemToDelete?.status === 'DRAFT') {
                const response = await new MyApiClient().deleteVenueSchedule(token, itemToDelete.id);
                if (response.error) {
                    setErrorMessage(response.error);
                } else {
                    chiudi();
                    await fetchDisponibilita();
                }
            }
        } catch (error) {
            console.error('Errore nell\'eliminazione:', error);
            setErrorMessage('Errore durante l\'eliminazione');
        } finally {
            setShowDeleteModal(false);
            setItemToDelete(null);
            setLoading(false);
        }
    };

    const confirmCancel = async () => {
        try {
            setLoading(true);
            const token = getToken();
            if (token && itemToCancel) {
                const response = await new MyApiClient().cancelVenueSchedule(token, itemToCancel.id);
                if (response.error) {
                    setErrorMessage(response.error);
                } else {
                    chiudi();
                    await fetchDisponibilita();
                }
            }
        } catch (error) {
            console.error('Errore nella cancellazione:', error);
            setErrorMessage('Errore durante la cancellazione');
        } finally {
            setShowCancelModal(false);
            setItemToCancel(null);
            setLoading(false);
        }
    };

    const updateOpeningHoursItem = (index, field, value) => {
        const updatedItems = formData.openingHoursItems.map((item, i) =>
            i === index ? { ...item, [field]: value } : item
        );
        setFormData({ ...formData, openingHoursItems: updatedItems });
    };

    const removeOpeningHoursItem = (index) => {
        if (seduteFascia(formData.openingHoursItems[index]).length > 0) return;
        setFormData({ ...formData, openingHoursItems: formData.openingHoursItems.filter((_, i) => i !== index) });
    };

    // Campi del modulo, uguali nel pannello e nella modal della nuova disponibilita
    const campi = (prefisso) => (
        <>
            <Row className="g-4">
                <Col md={6}>
                    <Form.Label htmlFor={`${prefisso}-inizio`}>Data inizio *</Form.Label>
                    <Form.Control
                        id={`${prefisso}-inizio`}
                        className="mt-2"
                        type="date"
                        value={formData.startDate}
                        onChange={(e) => setFormData({ ...formData, startDate: e.target.value })}
                    />
                </Col>
                <Col md={6}>
                    <Form.Label htmlFor={`${prefisso}-fine`}>Data fine *</Form.Label>
                    <Form.Control
                        id={`${prefisso}-fine`}
                        className="mt-2"
                        type="date"
                        value={formData.endDate}
                        onChange={(e) => setFormData({ ...formData, endDate: e.target.value })}
                    />
                </Col>
            </Row>

            <h5 className="sid-form-section mt-4 mb-2">Orari di apertura</h5>

            {formData.openingHoursItems.length > 0 && (
                <Row className="g-2 sid-disp-fasce__head">
                    <Col xs={5} md={4}><Form.Label className="m-0">Giorno</Form.Label></Col>
                    <Col xs={3}><Form.Label className="m-0">Apertura</Form.Label></Col>
                    <Col xs={3}><Form.Label className="m-0">Chiusura</Form.Label></Col>
                </Row>
            )}
            {formData.openingHoursItems.map((item, index) => {
                const impegnate = seduteFascia(item);
                const bloccata = impegnate.length > 0;
                return (
                    <div key={item.id || index} className={`sid-disp-fascia${bloccata ? ' is-locked' : ''}`}>
                        <Row className="g-2 align-items-center">
                            <Col xs={5} md={4}>
                                <Select
                                    aria-label="Giorno"
                                    styles={selectStyles}
                                    menuPortalTarget={document.body}
                                    placeholder=""
                                    isDisabled={bloccata}
                                    value={dayOptions.find(option => option.value === item.dayOfWeek) || null}
                                    onChange={(selectedOption) => updateOpeningHoursItem(index, 'dayOfWeek', selectedOption?.value || '')}
                                    options={dayOptions}
                                />
                            </Col>
                            <Col xs={3}>
                                <Form.Control
                                    id={`${prefisso}-apre-${index}`}
                                    aria-label="Apertura"
                                    type="time"
                                    disabled={bloccata}
                                    value={item.openTime}
                                    onChange={(e) => updateOpeningHoursItem(index, 'openTime', e.target.value)}
                                />
                            </Col>
                            <Col xs={3}>
                                <Form.Control
                                    id={`${prefisso}-chiude-${index}`}
                                    aria-label="Chiusura"
                                    type="time"
                                    disabled={bloccata}
                                    value={item.closeTime}
                                    onChange={(e) => updateOpeningHoursItem(index, 'closeTime', e.target.value)}
                                />
                            </Col>
                            <Col xs={1} md={2}>
                                {!bloccata && (
                                    <Button
                                        variant="outline-danger"
                                        size="sm"
                                        className="sid-btn-compact"
                                        aria-label="Rimuovi orario"
                                        onClick={async () => { if (await chiediConferma({ titolo: 'Togliere la fascia?', testo: 'La fascia di apertura sparisce.' })) removeOpeningHoursItem(index); }}
                                    >
                                        <Trash size="14" />
                                    </Button>
                                )}
                            </Col>
                        </Row>
                        {bloccata && collegamentiFascia(impegnate)}
                    </div>
                );
            })}
        </>
    );

    // Riga sotto la fascia impegnata: quante sedute e i prodotti da cui spostarle
    const collegamentiFascia = (eventi) => {
        const prodotti = [];
        const perChiave = {};
        let inAttesa = false;
        eventi.forEach((e) => {
            const p = risolti[chiaveGruppo(e)];
            if (!p) {
                inAttesa = true;
                return;
            }
            const k = `${p.tipo}|${p.id || chiaveGruppo(e)}`;
            if (!perChiave[k]) {
                perChiave[k] = { ...p, chiave: k, sedute: 0 };
                prodotti.push(perChiave[k]);
            }
            perChiave[k].sedute += 1;
        });
        const n = eventi.length;
        return (
            <div className="sid-disp-fascia__info">
                <span>{n} {n === 1 ? 'seduta prenotata' : 'sedute prenotate'} — prima spostale nei prodotti:</span>
                {prodotti.map((p) => (p.url ? (
                    <Button
                        key={p.chiave}
                        size="sm"
                        variant="outline-dark"
                        className="sid-btn-compact"
                        title={`${TIPI_PRODOTTO[p.tipo] || 'Evento'} · ${p.sedute} ${p.sedute === 1 ? 'seduta' : 'sedute'}`}
                        onClick={() => navigate(p.url)}
                    >
                        {p.tipo === 'corso' ? 'Vai al corso' : p.tipo === 'lezione' ? 'Vai alla lezione' : 'Vai alla prenotazione'}: {p.nome}
                    </Button>
                ) : (
                    <span key={p.chiave} className="sid-disp-fascia__prodotto">{p.nome}</span>
                )))}
                {inAttesa && <span className="text-muted">ricerca prodotti…</span>}
            </div>
        );
    };

    const esitoVerifica = () => {
        if (!verificaAttiva) return null;
        if (sedute.inCorso) return <span className="sid-check sid-check--wait">Verifica sedute in corso…</span>;
        if (sedute.errore) return <span className="sid-check sid-check--wait">{sedute.errore}</span>;
        if (scoperte.length === 0) return null;
        const elenco = scoperte.slice(0, 3).map(descriviSeduta).join('; ');
        return (
            <span className="sid-check sid-check--ko">
                {scoperte.length} {scoperte.length === 1 ? 'seduta prenotata resterebbe fuori' : 'sedute prenotate resterebbero fuori'}: {elenco}
                {scoperte.length > 3 ? ' …' : ''} Spostale prima nei prodotti (corsi, lezioni, prenotazioni).
            </span>
        );
    };

    const pannello = (item) => {
        const attiva = item.status === 'ACTIVE';
        const verificaKo = verificaAttiva && !sedute.inCorso && !sedute.errore && scoperte.length > 0;
        const seduteNelPeriodo = sedute.lista.length;
        const bloccaCancella = attiva && (sedute.inCorso || seduteNelPeriodo > 0);
        const esito = esitoVerifica();
        return (
            <div className="sid-occ-panel">
                {campi(`disp-${item.id}`)}
                {esito && <div className="mt-4">{esito}</div>}
                {errorMessage && <div className="sid-check sid-check--ko mt-4">{errorMessage}</div>}
                <div className="d-flex justify-content-end flex-wrap gap-2 mt-4">
                    <Button size="sm" className="btn btn-dark light sid-btn-compact" onClick={chiudi} disabled={loading}>
                        Annulla
                    </Button>
                    {attiva ? (
                        <>
                            <span
                                className="d-inline-block"
                                title={bloccaCancella ? 'Ci sono sedute prenotate in questo periodo: spostale o annullale prima nei prodotti' : undefined}
                            >
                                <Button
                                    size="sm"
                                    variant="outline-danger"
                                    className="sid-btn-compact"
                                    disabled={loading || bloccaCancella}
                                    onClick={() => { setItemToCancel(item); setShowCancelModal(true); }}
                                >
                                    Cancella disponibilità
                                </Button>
                            </span>
                            <Button
                                size="sm"
                                className="btn btn-primary sid-btn-compact"
                                onClick={handleSave}
                                disabled={loading || sedute.inCorso || verificaKo}
                            >
                                {loading ? 'Salvataggio…' : 'Salva'}
                            </Button>
                        </>
                    ) : (
                        <>
                            <Button
                                size="sm"
                                variant="outline-danger"
                                className="sid-btn-compact"
                                disabled={loading}
                                onClick={() => { setItemToDelete(item); setShowDeleteModal(true); }}
                            >
                                Elimina
                            </Button>
                            <Button size="sm" variant="outline-primary" className="sid-btn-compact" onClick={handleSave} disabled={loading}>
                                {loading ? 'Salvataggio…' : 'Salva come bozza'}
                            </Button>
                            <Button size="sm" className="btn btn-primary sid-btn-compact" onClick={handleSaveAndActivate} disabled={loading}>
                                Salva e attiva
                            </Button>
                        </>
                    )}
                </div>
            </div>
        );
    };

    // Pannello della nuova disponibilita, in cima all'elenco
    const pannelloNuova = () => (
        <div className="sid-occ-panel">
            {campi('disp-nuova')}
            {errorMessage && <div className="sid-check sid-check--ko mt-4">{errorMessage}</div>}
            <div className="d-flex justify-content-end flex-wrap gap-2 mt-4">
                <Button size="sm" className="btn btn-dark light sid-btn-compact" onClick={chiudi} disabled={loading}>
                    Annulla
                </Button>
                <Button size="sm" variant="outline-primary" className="sid-btn-compact" onClick={handleSave} disabled={loading}>
                    {loading ? 'Salvataggio…' : 'Salva come bozza'}
                </Button>
                <Button size="sm" className="btn btn-primary sid-btn-compact" onClick={handleSaveAndActivate} disabled={loading}>
                    Salva e attiva
                </Button>
            </div>
        </div>
    );

    const filteredDisponibilita = disponibilita.filter(item => item.status !== 'CANCELLED');
    const mostraTabella = filteredDisponibilita.length > 0 || (nuova && !readonly);

    return (
        <>
            <Card>
                {/* Niente titolo (ripeterebbe la tab "Disponibilita'"): solo il pulsante, a destra */}
                {!readonly && (
                    <Card.Header className="d-flex justify-content-end align-items-center">
                        <Button variant="primary" size="sm" className="btn-add" onClick={apriNuova} disabled={loading}>
                            <Add size="16" className="me-1" />
                            Nuova Disponibilità
                        </Button>
                    </Card.Header>
                )}
                <Card.Body>
                    {!mostraTabella ? (
                        <div className="text-center">
                            <Calendar size="64" className="text-muted mb-4" />
                            <h6 className="text-muted mb-3">Nessuna disponibilità configurata</h6>
                            <p className="text-muted mb-4">Inizia creando la tua prima disponibilità per l'impianto</p>
                            {!readonly && (
                                <Button variant="primary" size="lg" style={{ margin: '0 auto', display: 'flex', justifyContent: 'center', alignItems: 'center' }} onClick={apriNuova}>
                                    <Add size="20" className="me-2" />
                                    Aggiungi la prima disponibilità
                                </Button>
                            )}
                        </div>
                    ) : (
                        <div className="table-responsive">
                            <table className={`sid-plan-table${readonly ? ' sid-plan-table--static' : ''}`}>
                                <tbody>
                                    {nuova && !readonly && (
                                        <tr className="sid-plan-table__panel">
                                            <td colSpan={3}>{pannelloNuova()}</td>
                                        </tr>
                                    )}
                                    {filteredDisponibilita.map((item) => {
                                        const aperta = !readonly && editingItem?.id === item.id;
                                        const stato = statusLabels[item.status] || { label: item.status, cls: 'text-muted' };
                                        const orari = righeOrari(item.openingHoursItems);
                                        const interazione = readonly ? {} : {
                                            role: 'button',
                                            tabIndex: 0,
                                            onClick: () => apriPannello(item),
                                            onKeyDown: (e) => { if (e.key === 'Enter') apriPannello(item); }
                                        };
                                        return (
                                            <React.Fragment key={item.id}>
                                                <tr className={aperta ? 'is-open' : undefined} {...interazione}>
                                                    <td className="text-nowrap">{dataIt(item.startDate)} – {dataIt(item.endDate)}</td>
                                                    <td>
                                                        {orari.length > 0 ? orari.map((r) => (
                                                            <div key={r.giorno} className="sid-disp-orario">
                                                                <span>{r.giorno}</span> {r.fasce}
                                                            </div>
                                                        )) : <span className="text-muted">Nessun orario configurato</span>}
                                                    </td>
                                                    <td>
                                                        <div className="d-flex align-items-center">
                                                            <i className={`fa fa-circle me-1 ${stato.cls}`} />{stato.label}
                                                        </div>
                                                    </td>
                                                </tr>
                                                {aperta && (
                                                    <tr className="sid-plan-table__panel">
                                                        <td colSpan={3}>{pannello(item)}</td>
                                                    </tr>
                                                )}
                                            </React.Fragment>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    )}
                </Card.Body>
            </Card>

            {/* Modal di conferma eliminazione (solo bozze) */}
            <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)}>
                <Modal.Header closeButton>
                    <Modal.Title>Conferma Eliminazione</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    Sei sicuro di voler eliminare questa disponibilità? L'operazione non può essere annullata.
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowDeleteModal(false)}>
                        Annulla
                    </Button>
                    <Button variant="danger" onClick={handleDelete} disabled={loading}>
                        {loading ? (
                            <>
                                <Spinner animation="border" size="sm" className="me-2" />
                                Eliminando...
                            </>
                        ) : (
                            'Elimina'
                        )}
                    </Button>
                </Modal.Footer>
            </Modal>

            {/* Modal di conferma cancellazione (disponibilità attive) */}
            <Modal show={showCancelModal} onHide={() => setShowCancelModal(false)}>
                <Modal.Header closeButton>
                    <Modal.Title>Conferma Cancellazione</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    Sei sicuro di voler cancellare questa disponibilità attiva? L'operazione non può essere annullata.
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowCancelModal(false)}>
                        Annulla
                    </Button>
                    <Button variant="warning" onClick={confirmCancel} disabled={loading}>
                        {loading ? (
                            <>
                                <Spinner animation="border" size="sm" className="me-2" />
                                Cancellando...
                            </>
                        ) : (
                            'Cancella'
                        )}
                    </Button>
                </Modal.Footer>
            </Modal>
        </>
    );
};

export default DisponibilitaImpianto;
